import {
  Button,
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalFooter,
  ModalBody,
} from "@chakra-ui/react";
import { useCallback, useLayoutEffect } from "react";

import useScreenType from "react-screentype-hook";

import { TitleWrapper } from "./Styles";
import {
  purgeOrphanedChakraModalLayers,
  schedulePurgeOrphanedChakraModalLayers,
} from "../../utils/chakraModalCleanup";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  disablePrimaryButton?: boolean;
  primaryLoading?: boolean;
  modalText?: string;
  modalTitle?: string;
  modalSubtitle?: string;
  children?: React.ReactNode;
  secondaryButtonText?: string;
  primaryButtonText?: string;
  onPrimaryButtonClicked?: () => void;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "xs" | "3xl" | "4xl" | "5xl" | "6xl" | "full";
  scrollBehavior?: "inside";
  cypressModalHeaderSelector?: string;
  isCentered?: boolean;
};
export const Modal = ({
  isOpen,
  onClose,
  modalTitle,
  modalText,
  children,
  secondaryButtonText,
  primaryLoading,
  disablePrimaryButton,
  primaryButtonText,
  onPrimaryButtonClicked,
  modalSubtitle,
  cypressModalHeaderSelector,
  size = "xl",
  ...props
}: Props) => {
  const { isMobile } = useScreenType();

  const handleClose = useCallback(() => {
    purgeOrphanedChakraModalLayers({ force: true });
    onClose();
    schedulePurgeOrphanedChakraModalLayers();
  }, [onClose]);

  useLayoutEffect(() => {
    if (isOpen) return;
    purgeOrphanedChakraModalLayers({ force: true });
    const t = window.setTimeout(() => purgeOrphanedChakraModalLayers({ force: true }), 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <ChakraModal
        {...props}
        size={isMobile ? "full" : size}
        isOpen
        onClose={handleClose}
        onCloseComplete={() => purgeOrphanedChakraModalLayers({ force: true })}
        motionPreset="none"
        isCentered
        closeOnOverlayClick={true}
        closeOnEsc={true}
        blockScrollOnMount={false}
        preserveScrollBarGap={false}
      >
        <ModalOverlay bg="rgba(0, 0, 0, 0.5)" />
        <ModalContent margin="0">
          <ModalHeader>
            <TitleWrapper data-cy={cypressModalHeaderSelector}>{modalTitle}</TitleWrapper>
          </ModalHeader>
          {modalSubtitle && (
            <div style={{ paddingLeft: "24px" }}>
              <h2>{modalSubtitle}</h2>
            </div>
          )}
          <ModalCloseButton data-cy="cypress-modal-close-button-tap"/>
          {!children && <ModalBody>{modalText}</ModalBody>}
          {children && children}
          {(primaryButtonText || secondaryButtonText) && (
            <ModalFooter>
              {secondaryButtonText && (
                <Button mr={3} onClick={handleClose} data-cy="cypress-cancel-modal-button-tap">
                  {secondaryButtonText}
                </Button>
              )}
              {primaryButtonText && (
                <Button
                  data-cy="cypress-submit-modal-button-tap"
                  type="submit"
                  isLoading={primaryLoading}
                  isDisabled={primaryLoading || disablePrimaryButton}
                  onClick={onPrimaryButtonClicked}
                  colorScheme="primary"
                >
                  {primaryButtonText}{" "}
                </Button>
              )}
            </ModalFooter>
          )}
        </ModalContent>
      </ChakraModal>
    </>
  );
};
